import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { BroadcastFilters } from '../types/broadcast.types';
import type { BroadcastFilterMetaUser, BroadcastFilterProject } from '@/types/broadcast';
import { SlidersHorizontal, ChevronDown, Languages, Tag, Calendar, User, Folder } from 'lucide-react';
import { SearchableSelect } from '@/components/ui/searchable-select';

interface BroadcastFiltersProps {
    pages: any;
    categories: { id: number; name: string }[];
    /** Contas Meta vinculadas — pra filtrar broadcasts por quem conectou. */
    metaUsers?: BroadcastFilterMetaUser[];
    /** Projetos (resolvidos via apisistema) — pra filtrar broadcasts por projeto. */
    projects?: BroadcastFilterProject[];
    filters: BroadcastFilters;
    onFiltersChange: (filters: BroadcastFilters) => void;
    availableLanguages?: string[];
}

export default function BroadcastFiltersComponent({
    filters,
    onFiltersChange,
    pages,
    categories,
    metaUsers = [],
    projects = [],
    availableLanguages = [],
}: BroadcastFiltersProps) {
    const [isOpen, setIsOpen] = useState(false);

    const activeFilterCount = [
        filters.status,
        filters.page,
        filters.categoryId,
        filters.metaUserName,
        filters.projectId,
        filters.targetLanguage,
        filters.dateFrom,
    ].filter(Boolean).length;

    return (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50/50 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#0f172a]/5">
                        <SlidersHorizontal className="h-4 w-4 text-[#0f172a]" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">Filtros</h3>
                    {activeFilterCount > 0 && (
                        <span className="text-[11px] font-semibold bg-[#0f172a] text-white rounded-full px-2 py-0.5">
                            {activeFilterCount}
                        </span>
                    )}
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                <div className="overflow-hidden">
                    <div className="px-6 pb-6 pt-2">
                        {/* 9 colunas pra caber: data, status, página, categoria,
                            usuário Meta, projeto, idioma, ordenar, ordem.
                            Em telas menores quebra automaticamente. */}
                        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-9 gap-4">
                            {/* A partir de (data mínima) */}
                            <div className="space-y-2">
                                <Label htmlFor="dateFrom" className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    A partir de
                                </Label>
                                <Input
                                    id="dateFrom"
                                    type="date"
                                    value={filters.dateFrom || ''}
                                    onChange={(e) =>
                                        onFiltersChange({ ...filters, dateFrom: e.target.value || undefined })
                                    }
                                    className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20"
                                />
                            </div>

                            {/* Status */}
                            <div className="space-y-2">
                                <Label htmlFor="status" className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                                    Status
                                </Label>
                                <Select
                                    value={filters.status || 'all'}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, status: value === 'all' ? undefined : value as any })
                                    }
                                >
                                    <SelectTrigger id="status" className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue placeholder="Todos" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        <SelectItem value="pending">Pendente</SelectItem>
                                        <SelectItem value="processing">Enviando</SelectItem>
                                        <SelectItem value="sent">Enviado</SelectItem>
                                        <SelectItem value="partial">Enviado parcialmente</SelectItem>
                                        <SelectItem value="failed">Falhou</SelectItem>
                                        <SelectItem value="cancelled">Cancelado</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Página */}
                            <div className="space-y-2">
                                <Label htmlFor="page" className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                                    Página
                                </Label>
                                <Select
                                    value={filters.page ?? "all"}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, page: value === "all" ? undefined : value })
                                    }
                                >
                                    <SelectTrigger id="page" className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue placeholder="Todas" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas</SelectItem>
                                        {pages.map((p: any) => (
                                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Categoria — categoria interna da página (chatmeta_page_categories) */}
                            <div className="space-y-2">
                                <Label htmlFor="category" className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                    <Tag className="h-3 w-3" />
                                    Categoria
                                </Label>
                                <SearchableSelect
                                    value={filters.categoryId ? String(filters.categoryId) : 'all'}
                                    onValueChange={(value) =>
                                        onFiltersChange({
                                            ...filters,
                                            categoryId: value === 'all' ? null : Number(value),
                                        })
                                    }
                                    options={[
                                        { value: 'all', label: 'Todas' },
                                        ...categories.map(c => ({ value: String(c.id), label: c.name }))
                                    ]}
                                    placeholder="Todas"
                                    searchPlaceholder="Buscar categoria..."
                                    className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20"
                                />
                            </div>

                            {/* Usuário Meta — agrupado por nome (1 entry mesmo se a conta
                                estiver vinculada em N apps). Value/onChange usa o nome direto. */}
                            <div className="space-y-2">
                                <Label htmlFor="metaUser" className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                    <User className="h-3 w-3" />
                                    Usuário Meta
                                </Label>
                                <SearchableSelect
                                    value={filters.metaUserName || 'all'}
                                    onValueChange={(value) =>
                                        onFiltersChange({
                                            ...filters,
                                            metaUserName: value === 'all' ? null : value,
                                        })
                                    }
                                    options={[
                                        { value: 'all', label: 'Todos' },
                                        ...metaUsers.map(m => ({ value: m.name, label: m.name }))
                                    ]}
                                    placeholder="Todos"
                                    searchPlaceholder="Buscar usuário Meta..."
                                    className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20"
                                />
                            </div>

                            {/* Projeto — chatmeta_apps.project_id resolvido via apisistema */}
                            <div className="space-y-2">
                                <Label htmlFor="project" className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                    <Folder className="h-3 w-3" />
                                    Projeto
                                </Label>
                                <SearchableSelect
                                    value={filters.projectId ? String(filters.projectId) : 'all'}
                                    onValueChange={(value) =>
                                        onFiltersChange({
                                            ...filters,
                                            projectId: value === 'all' ? null : Number(value),
                                        })
                                    }
                                    options={[
                                        { value: 'all', label: 'Todos' },
                                        ...projects.map(p => ({ value: String(p.id), label: p.name }))
                                    ]}
                                    placeholder="Todos"
                                    searchPlaceholder="Buscar projeto..."
                                    className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20"
                                />
                            </div>

                            {/* Idioma de tradução */}
                            <div className="space-y-2">
                                <Label htmlFor="targetLanguage" className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                    <Languages className="h-3 w-3" />
                                    Idioma I.A
                                </Label>
                                <Select
                                    value={filters.targetLanguage || 'all'}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, targetLanguage: value === 'all' ? undefined : value })
                                    }
                                >
                                    <SelectTrigger id="targetLanguage" className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue placeholder="Todos" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        <SelectItem value="__none__">Sem tradução</SelectItem>
                                        {availableLanguages.map((lang) => (
                                            <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Ordenar por */}
                            <div className="space-y-2">
                                <Label htmlFor="sortBy" className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                                    Ordenar por
                                </Label>
                                <Select
                                    value={filters.sortBy || 'created_at'}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, sortBy: value as any })
                                    }
                                >
                                    <SelectTrigger id="sortBy" className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="created_at">Data de Criação</SelectItem>
                                        <SelectItem value="sent_count">Quantidade de Envios</SelectItem>
                                        <SelectItem value="success_rate">Taxa de Sucesso</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Ordem */}
                            <div className="space-y-2">
                                <Label htmlFor="sortOrder" className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                                    Ordem
                                </Label>
                                <Select
                                    value={filters.sortOrder || 'desc'}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, sortOrder: value as any })
                                    }
                                >
                                    <SelectTrigger id="sortOrder" className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="desc">Decrescente</SelectItem>
                                        <SelectItem value="asc">Crescente</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Clear filters — limpa tudo incluindo dateFrom (ver todos) */}
                        {activeFilterCount > 0 && (
                            <div className="mt-4 pt-3 border-t border-gray-50">
                                <button
                                    onClick={() => onFiltersChange({ sortBy: filters.sortBy, sortOrder: filters.sortOrder })}
                                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    Limpar filtros ativos
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}